"use client";

import type { FeatureSlideData } from "@gettactic/defines/src/feature-announcements";
import {
  useFeatureAnnouncementSession,
  type FeatureModalMode
} from "@gettactic/helpers";
import {
  useAuthenticated,
  useOrgFeatureFlag,
  useOrganizationSettings
} from "@gettactic/hooks";
import React, { createContext, useContext, useMemo } from "react";

interface FeatureAnnouncementContextType {
  openAllFeaturesModal: () => void;
  hasUnseenFeatures: boolean;
  unseenCount: number;
  // Modal state for rendering in AppMainLayout
  isModalOpen: boolean;
  currentFeature: FeatureSlideData | null;
  allFeatures: FeatureSlideData[] | null;
  modalMode: FeatureModalMode;
  handleCloseModal: () => void;
  handleDismissFeature: (featureId?: string, action?: "try" | "maybe-later") => void;
}

const FeatureAnnouncementContext =
  createContext<FeatureAnnouncementContextType | null>(null);

export const useFeatureAnnouncement = () => {
  const context = useContext(FeatureAnnouncementContext);
  if (!context) {
    throw new Error(
      "useFeatureAnnouncement must be used within FeatureAnnouncementProvider"
    );
  }
  return context;
};

export const FeatureAnnouncementProvider: React.FC<React.PropsWithChildren> = ({
  children
}) => {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const organizationId = authenticatedUser?.organization?.id;
  const { data: organizationSettings } = useOrganizationSettings({
    id: organizationId
  });

  const tessaFeatureFlag = useOrgFeatureFlag("tessa");
  const workplaceRequestsFeatureFlag = useOrgFeatureFlag("workplace_requests");

  // Wait for organization settings to load before determining available features
  const areFlagsLoaded = organizationSettings !== undefined;

  const enabledFlags = useMemo(
    () => ({
      tessa: tessaFeatureFlag ?? false,
      workplace_requests: workplaceRequestsFeatureFlag ?? false
    }),
    [tessaFeatureFlag, workplaceRequestsFeatureFlag]
  );

  const isAdmin = authenticatedUser?.permissions?.settings ?? false;

  const {
    isModalOpen,
    currentFeature,
    allFeatures,
    modalMode,
    hasUnseenFeatures,
    unseenCount,
    handleCloseModal,
    handleDismissFeature,
    openAllFeaturesModal
  } = useFeatureAnnouncementSession({ enabledFlags, areFlagsLoaded, isAdmin });

  const contextValue: FeatureAnnouncementContextType = {
    openAllFeaturesModal,
    hasUnseenFeatures,
    unseenCount,
    isModalOpen,
    currentFeature,
    allFeatures,
    modalMode,
    handleCloseModal,
    handleDismissFeature
  };

  return (
    <FeatureAnnouncementContext.Provider value={contextValue}>
      {children}
    </FeatureAnnouncementContext.Provider>
  );
};
